import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Typography, useTheme } from '@mui/material';
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import CustomButton from '../../components/@extended/CustomButton';
import WidgetSetting from './WidgetSetting';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import {
  bloqcibeApi,
  useGetOngoingTrialsQuery,
  useUpdateDashboardPreferenceMutation,
} from '../../store/slices/apiSlice';
import DragContainer2 from './DragContainer2';
import AEIncidentsAcrossSites from './widgets/AEIncidentsAcrossSites';
import EntityCount from './widgets/EntityCount';
import MaximizeWidget from './widgets/MaximizeWidget';
import SubjectDropoutRate from './widgets/SubjectDropoutRate';
import SitePerformanceMetricsTable from './widgets/SitePerformanceMetricsTable';
import SubjectEnrollmentTrend from './widgets/SubjectEnrollmentTrend';
import ScreeningFaliureRate from './widgets/ScreeningFailureRate';
import GMapContainer from './widgets/GMapContainer';
import SitePerformanceMetricsBarChart from './widgets/SitePerformanceMetricsBarChart';
import QueryStatisticsBarChart from './widgets/QueryStatisticsBarChart';
import AdvancedTherapiesUsedWidget from './widgets/AdvancedTherapiesUsedWidget';
import CDSTClassificationWidget from './widgets/CDSTClassificationWidget';
import VDZDiscontinuationWidget from './widgets/VDZDiscontinuationWidget';
import { getUserRole } from '../util';

// Roles that should see all sites selected by default
const ROLES_SELECT_ALL_SITES = ['site_monitor', 'data_manager'];

// Helper to get localStorage key for dashboard preferences
const getDashboardPrefsKey = (userId) => `dashboard_prefs_${userId}`;

// Helper to load dashboard preferences from localStorage
const loadDashboardPrefs = (userId) => {
  if (!userId) return null;
  try {
    const stored = localStorage.getItem(getDashboardPrefsKey(userId));
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading dashboard preferences:', error);
  }
  return null;
};

// Helper to save dashboard preferences to localStorage
const saveDashboardPrefs = (userId, trial, selectedSites, widgetsPerRow) => {
  if (!userId) return;
  try {
    localStorage.setItem(
      getDashboardPrefsKey(userId),
      JSON.stringify({ trial, selectedSites, widgetsPerRow })
    );
  } catch (error) {
    console.error('Error saving dashboard preferences:', error);
  }
};

const ReportingDashboard = (props) => {
  const theme = useTheme();
  const [openSetting, setOpenSetting] = useState(false);
  const sponsorId = useSelector((state) => state?.userDetails?.loggedInUser.sponsorId);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const { data: ongoingTrials, isLoading } = useGetOngoingTrialsQuery(sponsorId);
  const [getTrialSiteDetails] = bloqcibeApi.endpoints.getTrialSiteDetails.useLazyQuery();
  const [trial, setTrial] = useState('');
  const [selectedSites, setSelectedSites] = useState([]);
  const [siteList, setSiteList] = useState([]);
  const [widgetsPerRow, setWidgetsPerRow] = useState(3);
  const userPreferences = useSelector((state) => state?.userDetails?.loggedInUser?.dashboardPreferences);
  const userId = useSelector((state) => state.userDetails?.loggedInUser?.id);
  const [updateDashboardPreference] = useUpdateDashboardPreferenceMutation();

  // Auth state for role checking
  const { user: loggedInUser, userData } = useSelector((state) => state.auth);

  // Track if initial load from localStorage has been done
  const hasInitializedRef = useRef(false);
  // Track previous trial to detect actual changes
  const prevTrialRef = useRef('');
  // Track if sites were loaded from localStorage for current trial
  const sitesLoadedFromStorageRef = useRef(false);

  // Compute user role for the selected trial
  const userRole = useMemo(() => {
    if (!trial) return '';
    return getUserRole(loggedInUser, trial, null, userData);
  }, [loggedInUser, trial, userData]);

  // Check if user should see all sites by default
  const shouldSelectAllSites = useMemo(() => {
    return ROLES_SELECT_ALL_SITES.includes(userRole);
  }, [userRole]);

  const getContentByType = useCallback((type, additionalProps) => {
    const componentMap = {
      TRIAL_BASIC_ENTITY_DATA: EntityCount,
      TRIAL_SITE_AE_SAE_COUNT: AEIncidentsAcrossSites,
      TRIAL_SUBJECT_DROPOUT_RATE: SubjectDropoutRate,
      TRIAL_SITE_PERFORMANCE_DATA: SitePerformanceMetricsTable,
      TRIAL_SITE_PERFORMANCE_DATA_V2: SitePerformanceMetricsBarChart,
      TRIAL_SUBJECT_ENROLLMENT_TREND: SubjectEnrollmentTrend,
      TRIAL_SUBJECT_SCREEN_FAILURE_RATE: ScreeningFaliureRate,
      TRIAL_SITE_GEOGRAPHIC_DISTRIBUTION: GMapContainer,
      TRIAL_SITE_QUERY_STATISTICS: QueryStatisticsBarChart,
      // Balancing Criteria Widgets
      BALANCING_ADVANCED_THERAPIES_USED: AdvancedTherapiesUsedWidget,
      BALANCING_CDST_CLASSIFICATION: CDSTClassificationWidget,
      BALANCING_VDZ_DISCONTINUATION: VDZDiscontinuationWidget,
    };

    const Component = componentMap[type];
    if (Component) {
      return (
        <Component
          trial={trial}
          site={selectedSites}
          siteList={siteList}
          preference={type}
          maximizeWidget={(_widget) => setSelectedWidget(_widget)}
          {...additionalProps}
        />
      );
    } else {
      return null;
    }
  }, [trial, selectedSites, siteList]);

  const [widgetList, setWidgetList] = useState({});

  // Effect 1: Initialize trial from localStorage or default to first trial
  useEffect(() => {
    if (!ongoingTrials || ongoingTrials.length === 0 || !userId) return;
    if (hasInitializedRef.current) return;

    hasInitializedRef.current = true;
    const storedPrefs = loadDashboardPrefs(userId);

    if (storedPrefs?.widgetsPerRow != null && [1, 2, 3].includes(storedPrefs.widgetsPerRow)) {
      setWidgetsPerRow(storedPrefs.widgetsPerRow);
    }

    if (storedPrefs?.trial) {
      // Validate that stored trial still exists in ongoing trials
      const trialExists = ongoingTrials.some((t) => t.id === storedPrefs.trial);
      if (trialExists) {
        setTrial(storedPrefs.trial);
        // Mark that we should try to load sites from storage
        sitesLoadedFromStorageRef.current = true;
        return;
      }
    }

    // Fallback to first trial
    setTrial(ongoingTrials[0]?.id);
  }, [ongoingTrials, userId]);

  // Effect 2: Fetch sites when trial changes, apply role-based or stored site selection
  useEffect(() => {
    if (!trial || !sponsorId) return;

    const fetchSitesAndApplySelection = async () => {
      const _siteList = await getTrialSiteDetails({
        sponsorId,
        trialId: trial,
      });

      if (_siteList?.data) {
        const _list = _siteList.data.map((_site) => ({
          siteName: _site.site.orgname,
          id: _site.siteId,
          totalSubjects: _site.totalSubjects || 0,
        }));

        setSiteList(_list);

        // Determine which sites to select
        let sitesToSelect = [];
        const isTrialChange = prevTrialRef.current !== '' && prevTrialRef.current !== trial;
        const storedPrefs = loadDashboardPrefs(userId);

        // If we have stored sites for THIS trial and it's not a trial change, use them
        if (
          sitesLoadedFromStorageRef.current &&
          storedPrefs?.trial === trial &&
          storedPrefs?.selectedSites?.length > 0 &&
          !isTrialChange
        ) {
          // Validate stored sites still exist in current site list
          const validStoredSites = storedPrefs.selectedSites.filter((siteId) =>
            _list.some((s) => s.id === siteId)
          );
          if (validStoredSites.length > 0) {
            sitesToSelect = validStoredSites;
          }
        }

        // If no valid stored sites, apply role-based default
        if (sitesToSelect.length === 0) {
          // Get current role for this trial (need to compute inline since userRole may not be updated yet)
          const currentRole = getUserRole(loggedInUser, trial, null, userData);
          const shouldSelectAll = ROLES_SELECT_ALL_SITES.includes(currentRole);

          if (shouldSelectAll && _list.length > 0) {
            // Select all sites for site_monitor/data_manager
            sitesToSelect = _list.map((site) => site.id);
          } else if (_list.length > 0) {
            // Default to first site for other roles
            sitesToSelect = [_list[0].id];
          }
        }

        setSelectedSites(sitesToSelect);
        prevTrialRef.current = trial;
        sitesLoadedFromStorageRef.current = false; // Reset for future trial changes
      }
    };

    fetchSitesAndApplySelection();
  }, [trial, sponsorId, getTrialSiteDetails, loggedInUser, userData, userId]);

  // Effect 3: Build widget list when preferences, trial, or sites change
  useEffect(() => {
    if (userPreferences && trial && selectedSites.length > 0) {
      const filteredPreferences = userPreferences.filter((user) => getContentByType(user.preference.type));

      const updatedWidgetList = {};
      _.orderBy(filteredPreferences, (preference) => preference?.order).forEach((preference) => {
        const content = getContentByType(preference.preference.type, { trialId: trial, siteId: selectedSites });
        updatedWidgetList[preference.preference.type] = {
          preferenceId: preference.id,
          id: preference.preference.type,
          label: preference.preference.label,
          content: content,
          display: preference.visibility,
          order: preference.order,
        };
      });
      setWidgetList(updatedWidgetList);
    }
  }, [userPreferences, trial, selectedSites, getContentByType]);

  // Effect 4: Persist trial, site selection, and widgets per row to localStorage (debounced)
  useEffect(() => {
    if (!userId || !trial) return;
    // Only persist if we have valid selections
    if (selectedSites.length > 0) {
      saveDashboardPrefs(userId, trial, selectedSites, widgetsPerRow);
    }
  }, [userId, trial, selectedSites, widgetsPerRow]);

  // Handler for trial change from WidgetSetting - clears stored sites for new trial
  const handleTrialChange = useCallback((newTrial) => {
    if (newTrial !== trial) {
      sitesLoadedFromStorageRef.current = false; // Force role-based default for new trial
      setTrial(newTrial);
    }
  }, [trial]);

  // Handler for site selection change from WidgetSetting
  const handleSitesChange = useCallback((newSites) => {
    setSelectedSites(newSites);
  }, []);

  // Handler to select all sites (for WidgetSetting)
  const handleSelectAllSites = useCallback(() => {
    if (siteList.length > 0) {
      const allSiteIds = siteList.map((site) => site.id);
      setSelectedSites(allSiteIds);
    }
  }, [siteList]);

  const openWidgetSetting = () => {
    setOpenSetting(true);
  };
  const onCloseSetting = () => {
    setOpenSetting(false);
  };
  const onWidgetSelection = async (event, key) => {
    const newFilters = { ...widgetList };
    newFilters[key].display = event.target.checked;
    const payload = {
      userId,
      preferences: [{ id: newFilters[key].preferenceId, visibility: event.target.checked }],
    };
    setWidgetList(newFilters);

    await updateDashboardPreference(payload);
  };
  const onWidgetSelectAll = (event) => {
    const newFilters = { ...widgetList };
    Object.keys(newFilters).forEach((key) => {
      newFilters[key].display = event.target.checked;
    });
    setWidgetList(newFilters);
  };
  return (
    <>
      <Box sx={{ backgroundColor: '#FAFAFA' }}>
        {!selectedWidget && Object.keys(widgetList).length !== 0 && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2, alignItems: 'center', paddingLeft: 2, paddingRight: 2 }}>
              <Typography variant="h6" color="initial">
                Reporting Dashboard
              </Typography>
              <Box>
                <CustomButton startIcon={<SettingsIcon />} variant="outlined" onClick={openWidgetSetting}>
                  Configure
                </CustomButton>
              </Box>
            </Box>
            <DragContainer2
              userPreferences={userPreferences}
              widgets={widgetList}
              maximizeWidget={(_widget) => setSelectedWidget(_widget)}
              selectedSitesCount={selectedSites.length}
              widgetsPerRow={widgetsPerRow}
            />
          </>
        )}

        {selectedWidget && (
          <MaximizeWidget
            selectedWidget={selectedWidget}
            backToDashboard={() => setSelectedWidget(null)}
            trial={trial}
            site={selectedSites}
          />
        )}
        <WidgetSetting
          onClose={onCloseSetting}
          open={openSetting}
          onWidgetSelectAll={onWidgetSelectAll}
          onWidgetSelection={onWidgetSelection}
          widgets={widgetList}
          trial={trial}
          siteList={siteList}
          selectedSites={selectedSites}
          setTrial={handleTrialChange}
          setSelectedSites={handleSitesChange}
          userRole={userRole}
          onSelectAllSites={handleSelectAllSites}
          widgetsPerRow={widgetsPerRow}
          onWidgetsPerRowChange={setWidgetsPerRow}
        />
      </Box>
      {(!selectedWidget && !isLoading && Object.keys(widgetList).length === 0) && (
        <Box sx={{ height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 2,
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" color="initial">
              Reporting Dashboard
            </Typography>
          </Box>
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Box sx={{ textAlign: 'center', paddingY: 10 }}>
              <Typography variant="subtitle1" color={theme.palette.text.secondary} sx={{ pb: 0.5 }}>
                Dashboard Unavailable.
              </Typography>
              <Typography variant="body2" color={theme.palette.text.secondary} sx={{ pb: 0.5, fontStyle: 'italic' }}>
                Note: Contact System Admin for assigning dashboard preferences.
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default ReportingDashboard;
